import { useCallback } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUploadMutation } from "@/hooks/uploads/useUploadMutation";
import AgentSelection from "../AgentSelection";
import { UploadComponentProps } from "./types";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface UploadYoutubeProps extends UploadComponentProps {
  youtubeUrl: string;
  onYoutubeUrlChange: (url: string) => void;
}

const UploadYoutube = ({
  availableAgents,
  selectedAgentId,
  onAgentSelect,
  youtubeUrl,
  onYoutubeUrlChange,
  onUploadSuccess,
  onDeferredActionAdd,
}: UploadYoutubeProps) => {
  const youtubeExtractMutation = useUploadMutation("youtube", selectedAgentId, onUploadSuccess);

  const handleSubmit = useCallback(() => {
    if (!youtubeUrl.trim()) {
      toast.error("Please enter a YouTube URL");
      return;
    }

    if (onDeferredActionAdd) {
      onDeferredActionAdd({
        type: "youtube",
        data: {
          url: youtubeUrl,
          fileName: `YouTube: ${youtubeUrl}`,
        },
      });
      toast.success(`YouTube transcript will be extracted after genie creation`);
      onYoutubeUrlChange("");
    } else {
      youtubeExtractMutation.mutate({ url: youtubeUrl });
    }
  }, [youtubeUrl, onYoutubeUrlChange, onDeferredActionAdd, youtubeExtractMutation]);

  return (
    <>
      <AgentSelection
        agents={availableAgents}
        selectedAgentId={selectedAgentId}
        onAgentSelect={onAgentSelect}
      />
      <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto">
        <GenieIcon name="clapperboard" size={64} color="#4E9CFF" gradientEnabled className="mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Extract YouTube Transcript</h3>
        <p className="text-sm text-gray-600 mb-6 text-center">Extract captions and transcript from YouTube videos</p>

        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <Input
            type="url"
            placeholder="https://youtube.com/watch?v=..."
            value={youtubeUrl}
            onChange={(e) => onYoutubeUrlChange(e.target.value)}
            className="flex-1 h-11 sm:h-12 text-sm sm:text-base"
            disabled={youtubeExtractMutation.isPending}
          />
          <Button
            onClick={handleSubmit}
            disabled={!youtubeUrl || youtubeExtractMutation.isPending}
            className="w-full sm:w-auto bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold px-6 sm:px-8 h-11 sm:h-12 shadow-lg hover:shadow-xl transition-all hover:scale-105 rounded-xl"
          >
            {youtubeExtractMutation.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Extract"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default UploadYoutube;
